<script setup lang="ts">
import { Info, CheckCircle2, AlertTriangle, AlertCircle } from 'lucide-vue-next'
export type Severity = 'success' | 'info' | 'warn' | 'error' | 'secondary' | 'contrast'

defineOptions({
  inheritAttrs: false
})

const props = withDefaults(
  defineProps<{
    img?: string
    text?: string
    severity: Severity
  }>(),
  {
    severity: 'info',
    text: ''
  }
)

const slots = useSlots()
const attrs = useAttrs()

const severityClasses: Record<Severity, string> = {
  info: 'border-blue-200 bg-blue-50 text-blue-700 *:data-[slot=alert-description]:text-blue-700',
  success: 'border-green-200 bg-green-50 text-green-700 *:data-[slot=alert-description]:text-green-700',
  warn: 'border-yellow-200 bg-yellow-50 text-yellow-700 *:data-[slot=alert-description]:text-yellow-700',
  error: 'border-red-200 bg-red-50 text-red-700 *:data-[slot=alert-description]:text-red-700',
  secondary: 'border-border bg-muted text-foreground *:data-[slot=alert-description]:text-foreground',
  contrast: 'border-neutral-800 bg-neutral-900 text-neutral-100 *:data-[slot=alert-description]:text-neutral-100'
}

const severityIcons: Record<Severity, typeof Info> = {
  info: Info,
  success: CheckCircle2,
  warn: AlertTriangle,
  error: AlertCircle,
  secondary: Info,
  contrast: Info
}

const iconComponent = computed(() => severityIcons[props.severity])
</script>

<template>
  <Alert v-bind="attrs" variant="default" :class="['grid-cols-[auto_1fr] gap-x-3', severityClasses[severity]]">
    <!-- Custom image icon (priority over severity icon) -->
    <img v-if="img" :src="img" class="aspect-square size-5" />

    <!-- Custom icon slot (must render a direct SVG child e.g. a lucide icon) -->
    <slot v-else-if="slots.icon" name="icon" />

    <!-- Auto severity icon -->
    <component :is="iconComponent" v-else />

    <AlertDescription>
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div class="w-full font-normal text-sm">
          <slot name="text">
            <span v-if="text">{{ text }}</span>
          </slot>
        </div>

        <div v-if="slots['button']" class="w-max">
          <slot name="button" />
        </div>
      </div>
    </AlertDescription>
  </Alert>
</template>
